// src/components/AddressInputs.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  TextField,
  Button,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import Divider from "@mui/material/Divider";
import { useJsApiLoader } from "@react-google-maps/api";
import { useDebouncedCallback } from "use-debounce";
import "./AddressInputs.css";

const libs = ["places"];
const AUTOCOMPLETE_OPTIONS = {
  componentRestrictions: { country: "IN" },
  types: ["geocode"], // address predictions only
};

export default function AddressInputs({
  rideType,
  mode,
  terminal,
  pickup,
  dropOff,
  onPickupChange,
  onDropOffChange,
  editingField,   // "pickup" | "dropOff" | undefined
  onRowClick,     // (field) -> void
  onFieldSelect,  // (field, name, [lng,lat]) -> void
  onCancel,
  apiKey,         // place your key here or via env & prop
}) {
  // 1. Load Maps JS API
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyDRlRqPY-8EXbEpwfaRTi7xMdJrKznQ9rw",
    libraries: libs,
  });

  // 2. Local UI state
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  // 3. Refs for Maps services
  const acServiceRef    = useRef(null);
  const placesSvcRef    = useRef(null);

  // 4. Initialize services once API loaded
  useEffect(() => {
    if (isLoaded && !acServiceRef.current) {
      acServiceRef.current = new window.google.maps.places.AutocompleteService();
      // off-DOM container is fine for getting details
      placesSvcRef.current = new window.google.maps.places.PlacesService(
        document.createElement("div")
      );
    }
  }, [isLoaded]);

  // 5. Reset on field toggle
  useEffect(() => {
    if (!editingField) {
      setQuery("");
      setSuggestions([]);
    } else if (editingField === "pickup") {
      setQuery(pickup === `Terminal ${terminal}` ? "" : pickup);
    } else {
      setQuery(dropOff);
    }
  }, [editingField, pickup, dropOff, terminal]);

  // 6. Debounced prediction fetch
  const fetchPredictions = useDebouncedCallback((input) => {
    if (!input || !acServiceRef.current) {
      setSuggestions([]);
      return;
    }
    acServiceRef.current.getPlacePredictions(
      { input, ...AUTOCOMPLETE_OPTIONS },
      (preds, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          setSuggestions(
            preds.map((p) => ({
              id: p.place_id,
              description: p.description,
              place_id: p.place_id,
            }))
          );
        } else {
          setSuggestions([]);
        }
      }
    );
  }, 300);

  // 7. Trigger predictions when query or field changes
  useEffect(() => {
    if (editingField) fetchPredictions(query);
  }, [query, editingField, fetchPredictions]);

  // 8. User selects one suggestion
  const handleSelect = async ({ description, place_id }) => {
    let coords;
    // fetch geometry
    if (placesSvcRef.current && place_id) {
      const detailReq = { placeId: place_id, fields: ["geometry"] };
      coords = await new Promise((res) =>
        placesSvcRef.current.getDetails(detailReq, (resDetails, status) => {
          if (
            status ===
            window.google.maps.places.PlacesServiceStatus.OK &&
            resDetails.geometry
          ) {
            res([
              resDetails.geometry.location.lng(),
              resDetails.geometry.location.lat(),
            ]);
          } else res(undefined);
        })
      );
    }

    // propagate upstream
    if (editingField === "pickup") onPickupChange(description);
    else onDropOffChange(description);
    onFieldSelect(editingField, description, coords);

    // update local UI
    setQuery(description);
    setSuggestions([]);

    // auto-switch for outstation/intracity
    const needsBoth = ["outstation", "intracity"].includes(rideType);
    if (needsBoth) {
      if (editingField === "pickup" && !dropOff) {
        onRowClick("dropOff");
        return;
      }
      if (editingField === "dropOff" && !pickup) {
        onRowClick("pickup");
        return;
      }
    }

    onCancel();
  };

  // 9. Abort if error or loading
  if (loadError) {
    console.error("Google Maps load error", loadError);
    return null;
  }
  if (!isLoaded) return null;

  // 10. Fixed flags
  const isPickupFixed = rideType === "airport" && mode === "pickup";
  const isDropFixed   = rideType === "airport" && mode === "drop";

  // 11. Render helper
  const renderField = (field) => {
    const isFixed = field === "pickup" ? isPickupFixed : isDropFixed;
    const value   = field === "pickup" ? pickup : dropOff;
    const placeholder =
      field === "pickup"
        ? "Type pickup location..."
        : "Type drop-off location...";
    const iconSrc = field === "pickup" ? "/icons/web_pickup.svg" : "/icons/web_drop.svg";
    const colorCls = field === "pickup" ? "custom-green" : "custom-red";

    if (editingField === field && !isFixed) {
      return (
        <Box className="addr-input editing">
          <img src={iconSrc} className={`addr-icon ${colorCls}`} alt={field} />
          <TextField
            fullWidth
            autoFocus
            variant="standard"
            placeholder={placeholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{ disableUnderline: true }}
            className="addr-search-input"
          />
          {query && (
            <IconButton
              size="small"
              className="addr-clear-btn"
              onClick={(e) => {
                e.stopPropagation();
                setQuery("");
                setSuggestions([]);
              }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      );
    }

    return (
      <Box
        className={`addr-input ${isFixed ? "disabled" : "clickable"}`}
        onClick={() => !isFixed && onRowClick(field)}
      >
        <img src={iconSrc} className={`addr-icon ${colorCls}`} alt={field} />
        <Typography className="addr-text">
          {isFixed
            ? `Terminal ${terminal}`
            : value || (field === "pickup" ? "Enter pickup location" : "Enter drop-off location")}
        </Typography>
        {!isFixed && value && (
          <IconButton
            size="small"
            className="addr-clear-btn"
            onClick={(e) => {
              e.stopPropagation();
              field === "pickup" ? onPickupChange("") : onDropOffChange("");
            }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    );
  };

  return (
    <Paper className="addr-card" elevation={1}>
      {/* Pickup */}
      <Box className="addr-field">
        <Typography className="addr-label">Where should we pick you up?</Typography>
        {renderField("pickup")}
      </Box>

      {rideType !== "rental" && <Divider sx={{ mt: 1, mb: 2, borderColor: "#E0E0E0" }} />}

      {/* Drop-off */}
      {rideType !== "rental" && (
        <Box className="addr-field" style={{ borderBottom: "none" }}>
          <Typography className="addr-label">Where would you like to be dropped?</Typography>
          {renderField("dropOff")}
        </Box>
      )}

      {/* Suggestions */}
      {editingField && query.trim() && suggestions.length > 0 && (
        <Box className="addr-suggestions">
          {suggestions.map((s) => (
            <Box
              key={s.id}
              className="addr-suggestion"
              onClick={() => handleSelect(s)}
            >
              {s.description}
            </Box>
          ))}
          <Button fullWidth onClick={onCancel} className="addr-cancel-btn">
            Cancel
          </Button>
        </Box>
      )}
    </Paper>
  );
}
